import BlogInertiaController from '@/actions/App/Http/Controllers/BlogInertiaController';
import { BlogFilters } from '@/components/blog/blog-filters';
import { BlogMasonryGrid } from '@/components/blog/blog-masonry-grid';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Pagination } from '@/components/ui/pagination';
import { Typo } from '@/components/ui/typography';
import { WebBreadcrumb } from '@/components/web/layouts/web-breadcrumb';
import WebLayout from '@/components/web/layouts/web-layout';
import { BlogCategory, Post, Tag } from '@/types/model';
import { Head, router } from '@inertiajs/react';
import { FileText, X } from 'lucide-react';
import { useState } from 'react';

interface BlogIndexPageProps {
  pageTitle: string;
  seoDescription: string;
  posts: {
    data: Post[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: Array<{ url: string | null; label: string; active: boolean }>;
  };
  categories: BlogCategory[];
  tags: Tag[];
  trendingPosts: Post[];
  filters: {
    category?: string;
    tags?: string[];
  };
}

// Breadcrumb items
const breadcrumbItems = [{ title: 'Tin tức', href: '/tin-tuc' }];

export default function BlogIndex({ pageTitle, seoDescription, posts, categories, tags, trendingPosts, filters }: BlogIndexPageProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleApplyFilters = (newFilters: { category?: string; tags?: string[] }) => {
    setIsLoading(true);
    router.get(
      BlogInertiaController.index().url,
      {
        category: newFilters.category,
        tags: newFilters.tags,
      },
      {
        preserveState: true,
        preserveScroll: false,
        onFinish: () => setIsLoading(false),
      },
    );
  };

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    router.get(
      BlogInertiaController.index().url,
      {
        ...filters,
        page,
      },
      {
        preserveState: true,
        preserveScroll: false,
        onFinish: () => setIsLoading(false),
      },
    );
  };

  const clearFilter = (type: 'category' | 'tag', value?: string) => {
    const newFilters = { ...filters };

    if (type === 'category') {
      delete newFilters.category;
    } else if (type === 'tag' && value) {
      newFilters.tags = newFilters.tags?.filter((t) => t !== value);
      if (newFilters.tags?.length === 0) {
        delete newFilters.tags;
      }
    }

    setIsLoading(true);
    router.get(BlogInertiaController.index().url, newFilters, {
      preserveState: true,
      preserveScroll: false,
      onFinish: () => setIsLoading(false),
    });
  };

  const clearAllFilters = () => {
    setIsLoading(true);
    router.get(
      BlogInertiaController.index().url,
      {},
      {
        preserveState: true,
        preserveScroll: false,
        onFinish: () => setIsLoading(false),
      },
    );
  };

  const hasActiveFilters = filters.category || (filters.tags && filters.tags.length > 0);

  return (
    <WebLayout>
      {/* SEO Meta Tags */}
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={BlogInertiaController.index().url} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: pageTitle,
            description: seoDescription,
            blogPost: posts.data.map((post) => ({
              '@type': 'BlogPosting',
              headline: post.title,
              description: post.excerpt,
              image: post.featured_image,
              datePublished: post.published_at,
            })),
          })}
        </script>
      </Head>

      {/* Hero Section */}
      <section className="from-primary/10 mb-8 bg-gradient-to-r to-purple-500/10 py-12">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6 text-left">
            <Typo variant="h2">{pageTitle}</Typo>
            <Typo variant="muted" className="text-muted-foreground mb-6">
              {seoDescription}
            </Typo>
          </div>
          <div className="flex justify-start">
            <BlogFilters categories={categories} tags={tags} currentFilters={filters} onApplyFilters={handleApplyFilters} />
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="container mx-auto mb-4 max-w-5xl px-4 py-4 lg:px-0">
        <WebBreadcrumb items={breadcrumbItems} />
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-5xl px-4 pb-16 lg:px-0">
        <div className="lg:col-span-3">
          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">Lọc theo:</span>
              {filters.category && (
                <Badge variant="secondary" className="border-primary/20 gap-2">
                  {categories.find((c) => c.slug === filters.category)?.name}
                  <button onClick={() => clearFilter('category')} className="hover:text-destructive" aria-label="Xóa bộ lọc danh mục">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filters.tags?.map((tag) => (
                <Badge key={tag} variant="secondary" className="border-primary/20 gap-2">
                  {tags.find((t) => t.slug === tag)?.name}
                  <button onClick={() => clearFilter('tag', tag)} className="hover:text-destructive" aria-label="Xóa bộ lọc thẻ">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-muted-foreground hover:text-foreground">
                Xóa tất cả
              </Button>
            </div>
          )}

          {/* Results Count */}
          {!isLoading && posts.data.length > 0 && (
            <div className="mb-6">
              <Typo variant="muted">Hiển thị {posts.total} bài viết</Typo>
            </div>
          )}

          {/* Masonry Grid */}
          <BlogMasonryGrid posts={posts.data} isLoading={isLoading} />

          {/* Empty State */}
          {!isLoading && posts.data.length === 0 && (
            <Card className="p-12 text-center">
              <FileText className="text-muted-foreground mx-auto mb-4 h-16 w-16" />
              <Typo variant="h3" className="mb-2">
                Không tìm thấy bài viết nào
              </Typo>
              <Typo variant="muted" className="mb-4">
                Thử điều chỉnh bộ lọc hoặc xóa bộ lọc để xem tất cả bài viết
              </Typo>
              <Button variant="outline" onClick={clearAllFilters}>
                Xóa bộ lọc
              </Button>
            </Card>
          )}

          {/* Pagination */}
          {!isLoading && posts.last_page > 1 && (
            <div className="mt-12 flex justify-center">
              <Pagination currentPage={posts.current_page} lastPage={posts.last_page} onPageChange={handlePageChange} />
            </div>
          )}
        </div>
      </div>
    </WebLayout>
  );
}
